import React from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Stack,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const affiliateLinks = [
    {
        id: 'hike-bike-paddle',
        title: "Hike, Bike, Paddle",
        url: 'https://hikebikepaddle.com/',
        description:
            'A site with information on the Pinhoti Trail, the Chief Ladiga Trail, and Terrapin Creek. Three great things to check out in Piedmont, Alabama! Designed by Piedmont resident Kevin Cunningham.',
        type: 'Local info',
    },
    {
        id: 'troy-angela-ladiga',
        title: "Troy and Angela's Little Adventures Ladiga Trail Tour",
        url: 'https://www.youtube.com/watch?v=b5FxV85ilTo',
        description:
            'Ladiga Trail tour with Troy and Angela from Troy and Angela’s Little Adventures.',
        type: 'Video tour',
    },
    {
        id: 'troy-angela',
        title: "Troy and Angela's Little Adventures Robert's Home Fall Tour",
        url: 'https://www.youtube.com/watch?v=ottuvRYwo-k',
        description: "Robert's Home Fall tour with Troy and Angela from Troy and Angela’s Little Adventures.",
        type: 'Video tour',
    },
    {
        id: 'troy-angela-roberts-christmas',
        title: "Troy and Angela's Little Adventures Robert's Home Christmas Tour",
        url: 'https://www.youtube.com/watch?v=O3OitFhUh9A&t=251s',
        description:
            "Robert's Home Christmas tour with Troy and Angela from Troy and Angela’s Little Adventures.",
        type: 'Video tour',
    },
    {
        id: 'american-rails',
        title: 'American-Rails.com – Railroad History & Rail-Trails',
        url: 'https://www.american-rails.com/',
        description:
            'Educational resource about American railroads, classic passenger trains, fallen flags, and rail-trails across the United States.',
        type: 'Rail history resource',
    },
    {
        id: 'sacred-space-garden',
        title: 'Sacred Space Garden – "Timeless Seasons in the Sun"',
        url: 'https://www.piedmonthc.com/the-garden',
        description:
            'Take the audio tour of the Sacred Space Garden, "Timeless Seasons in the Sun," at the Piedmont Health Care Center on Dailey Street!',
        type: 'Audio tour',
    },
    {
        id: 'coleman-concierge',
        title: 'Calhoun County Alabama Bike Getaway',
        url: 'https://www.colemanconcierge.com/calhoun-county-alabama-bike-getaway/',
        description:
            'Information about Calhoun County bike destinations, including routes that connect to area trails.',
        type: 'Travel guide',
    },
    {
        id: 'silver-comet',
        title: 'SilverCometGA – Chief Ladiga Trail',
        url: 'http://www.silvercometga.com/chief-ladiga-trail/index-ladiga.shtml',
        description:
            'Overview of the Chief Ladiga Trail and its connection with the Silver Comet Trail, including maps and mileage.',
        type: 'Trail resource',
    },
    {
        id: 'wikipedia-clt',
        title: 'Chief Ladiga Trail – Wikipedia',
        url: 'https://en.wikipedia.org/wiki/Chief_Ladiga_Trail',
        description:
            'Background, history, and general information about the Chief Ladiga Trail.',
        type: 'Background info',
    },
    {
        id: 'piedmont-city',
        title: 'Chief Ladiga Trail – City of Piedmont',
        url: 'https://www.piedmontcity.org/chief-ladiga-trail/',
        description:
            'Official City of Piedmont information on the Chief Ladiga Trail, local access points, and visitor details.',
        type: 'Local info',
    },
    {
        id: 'alabama-travel',
        title: 'Chief Ladiga Trail / Eubanks Welcome Center',
        url: 'http://alabama.travel/places-to-go/chief-ladiga-trail-eubanks-welcome-center',
        description:
            'Alabama Tourism listing for the Chief Ladiga Trail and Eubanks Welcome Center.',
        type: 'Tourism',
    },
    {
        id: 'rails-to-trails',
        title: 'Rails-to-Trails: Chief Ladiga Trail',
        url: 'https://www.railstotrails.org/trailblog/alabamas-chief-ladiga-trail-june-2025-trail-of-the-month/',
        description:
            'Rails-to-trails resource with highlights and practical notes about the Chief Ladiga Trail.',
        type: 'Trail resource',
    },
    {
        id: 'jsu-epic',
        title: 'Chief Ladiga Trail – JSU EPIC',
        url: 'https://www.jsu.edu/epic/docs/CLT_access.html',
        description:
            'Jacksonville State University EPIC program information and resources related to the Chief Ladiga Trail.',
        type: 'University partner',
    },
];

export default function Links() {
    return (
        <Box sx={{ bgcolor: '#ffffff', py: { xs: 5, md: 7 } }}>
            <Container maxWidth="md">
                {/* Page header */}
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '2rem', sm: '2.4rem' },
                            color: '#002F6C',
                            mb: 1,
                        }}
                    >
                        Links & Affiliates
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ maxWidth: 620, mx: 'auto' }}
                    >
                        Explore partner sites, trail resources, and travel guides connected to the
                        Chief Ladiga Trail, Piedmont, and our wider community.
                    </Typography>
                </Box>

                {/* One-column stack so every card is the same width */}
                <Stack spacing={{ xs: 2.5, md: 3 }} alignItems="stretch">
                    {affiliateLinks.map((link) => (
                        <Card
                            key={link.id}
                            elevation={2}
                            sx={{
                                width: '100%',
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <CardContent
                                sx={{
                                    flexGrow: 1,
                                    pb: 1.5,
                                    '&:last-child': { pb: 1.5 },
                                }}
                            >
                                {link.type && (
                                    <Chip
                                        label={link.type}
                                        size="small"
                                        sx={{
                                            bgcolor: '#002F6C',
                                            color: '#fff',
                                            fontSize: '0.75rem',
                                            mb: 1,
                                        }}
                                    />
                                )}

                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 1,
                                        color: '#002F6C',
                                        fontSize: { xs: '1rem', sm: '1.05rem' },
                                    }}
                                >
                                    {link.title}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem' } }}
                                >
                                    {link.description}
                                </Typography>
                            </CardContent>

                            {/* Fixed‑width button so all “boxes” match */}
                            <Box
                                sx={{
                                    px: 2,
                                    pb: 2,
                                    pt: 0,
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Button
                                    component="a"
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="contained"
                                    endIcon={<OpenInNewIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        bgcolor: '#002F6C',
                                        '&:hover': { bgcolor: '#001F4C' },
                                        borderRadius: 999,
                                        px: 2.5,
                                        py: 0.75,
                                        fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                        minHeight: 0,
                                        width: 140, // all buttons the same width
                                    }}
                                >
                                    Visit site
                                </Button>
                            </Box>
                        </Card>
                    ))}
                </Stack>
            </Container>
        </Box>
    );
}
